import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import Styles from './PieChart.module.scss'

interface DataPoint {
  name: string
  value: number
  colors: {
    border: string
    inner: string
  }
}

interface PieChartProps {
  data: DataPoint[]
}

interface TooltipData {
  name: string
  value: number
  x: number
  y: number
}

const PieChart: React.FC<PieChartProps> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null)

  useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        const width = wrapperRef.current.clientWidth
        setDimensions({ width, height: width })
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (dimensions.width === 0 || !data.length) return

    d3.select(svgRef.current).selectAll('*').remove()

    const margin = { top: 20, right: 20, bottom: 20, left: 20 }
    const innerWidth = dimensions.width - margin.left - margin.right
    const innerHeight = dimensions.height - margin.top - margin.bottom
    const radius = Math.min(innerWidth, innerHeight) / 2
    const borderWidth = radius * 0.15

    const svg = d3
      .select(svgRef.current)
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .attr('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')

    const g = svg.append('g').attr('transform', `translate(${dimensions.width / 2},${dimensions.height / 2})`)

    g.append('circle').attr('r', radius).attr('fill', 'white')

    const pie = d3
      .pie<DataPoint>()
      .value((d) => d.value)
      .sort(null)

    const borderArc = d3
      .arc<d3.PieArcDatum<DataPoint>>()
      .innerRadius(radius - borderWidth)
      .outerRadius(radius)

    const innerArc = d3
      .arc<d3.PieArcDatum<DataPoint>>()
      .innerRadius(0)
      .outerRadius(radius - borderWidth)

    const pieData = pie(data)

    const showTooltip = (event: MouseEvent, d: d3.PieArcDatum<DataPoint>) => {
      if (!svgRef.current || !wrapperRef.current) return

      // Get SVG's position relative to viewport
      const svgRect = svgRef.current.getBoundingClientRect()

      // Calculate centroid in SVG coordinates
      const [cx, cy] = borderArc.centroid(d)

      // Map SVG coordinates to client coordinates
      const x = svgRect.left + svgRect.width / 2 + cx
      const y = svgRect.top + svgRect.height / 2 + cy

      // Get wrapper's position
      const wrapperRect = wrapperRef.current.getBoundingClientRect()

      // Calculate final position relative to wrapper
      setTooltipData({
        name: d.data.name,
        value: d.data.value,
        x: x - wrapperRect.left,
        y: y - wrapperRect.top,
      })
    }

    const hideTooltip = () => {
      setTooltipData(null)
    }

    // Add border arcs
    const borderPaths = g
      .selectAll<SVGPathElement, d3.PieArcDatum<DataPoint>>('.border-arc')
      .data(pieData)
      .enter()
      .append('path')
      .attr('d', (d) => borderArc(d))
      .attr('fill', (d) => d.data.colors.border)
      .attr('class', Styles.arc)

    borderPaths.on('mouseover', showTooltip).on('mouseout', hideTooltip)

    // Add inner arcs
    g.selectAll<SVGPathElement, d3.PieArcDatum<DataPoint>>('.inner-arc')
      .data(pieData)
      .enter()
      .append('path')
      .attr('d', (d) => innerArc(d))
      .attr('fill', (d) => d.data.colors.inner)
      .attr('class', Styles.arc)
      .on('mouseover', showTooltip)
      .on('mouseout', hideTooltip)

    // Add percentage labels
    const fontSize = radius * 0.1
    g.selectAll<SVGTextElement, d3.PieArcDatum<DataPoint>>('.label')
      .data(pieData)
      .enter()
      .append('text')
      .attr('transform', (d) => {
        const angle = (d.startAngle + d.endAngle) / 2
        const labelRadius = radius * 0.5
        const x = Math.cos(angle - Math.PI / 2) * labelRadius
        const y = Math.sin(angle - Math.PI / 2) * labelRadius
        return `translate(${x},${y})`
      })
      .attr('class', Styles.label)
      .style('--fontSize', `${fontSize}px`)
      .text((d) => `${d.value.toFixed(2)}%`)
      .on('mouseover', showTooltip)
      .on('mouseout', hideTooltip)
  }, [data, dimensions])

  return (
    <div ref={wrapperRef} className={Styles.wrapper}>
      <div className={Styles.chartContainer}>
        <svg ref={svgRef} className={Styles.svg} aria-label="Pie chart" />
      </div>
      {tooltipData && (
        <div
          className={Styles.tooltip}
          style={{
            position: 'absolute',
            left: `${tooltipData.x}px`,
            top: `${tooltipData.y}px`,
          }}
        >
          <div className={Styles.tooltipContent}>
            <span>{tooltipData.name}</span>
            <span>{tooltipData.value.toFixed(2)}%</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default PieChart
